// Dense ops queue for follow-up callbacks. Buckets: Overdue, Today, Upcoming,
// Completed. One-click actions: Call (loads lead into softphone), Snooze,
// Mark done, Cancel, Open lead.

import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type {
  CallbackBucket,
  CallbackDTO,
  Lead,
  SnoozeToken,
} from '@marnic/shared';
import { formatUKForDisplay } from '@marnic/shared';
import { api } from '../lib/api';
import { extractApiErrorMessage } from '../lib/api-error';
import { useAuthStore } from '../store/auth';
import { useSoftphone } from '../store/softphone';
import { softphoneService } from '../softphone/service';
import { getSocket } from '../lib/socket';

interface ListResponse {
  total: number;
  items: CallbackDTO[];
}

type Scope = 'mine' | 'team';

const BUCKETS: CallbackBucket[] = ['overdue', 'today', 'upcoming', 'completed'];

const BUCKET_LABEL: Record<CallbackBucket, string> = {
  overdue: 'Overdue',
  today: 'Today',
  upcoming: 'Upcoming',
  completed: 'Completed',
};

export function Callbacks(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isAgent = user?.role === 'agent';
  const [scope, setScope] = useState<Scope>('mine');
  const qc = useQueryClient();

  // Refetch all bucket lists when a callback changes elsewhere.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onChanged = (): void => {
      void qc.invalidateQueries({ queryKey: ['callbacks'] });
    };
    socket.on('callback_changed', onChanged);
    return () => {
      socket.off('callback_changed', onChanged);
    };
  }, [qc]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Callbacks</h1>
          <p className="text-sm text-ink-500">Today's follow-ups, in priority order.</p>
        </div>
        {!isAgent && (
          <div className="flex items-center gap-2">
            <select
              className="input"
              value={scope}
              onChange={(e) => setScope(e.target.value as Scope)}
            >
              <option value="mine">Mine</option>
              <option value="team">Team</option>
            </select>
          </div>
        )}
      </header>

      {BUCKETS.map((bucket) => (
        <BucketSection key={bucket} bucket={bucket} scope={scope} />
      ))}
    </div>
  );
}

function BucketSection({ bucket, scope }: { bucket: CallbackBucket; scope: Scope }): JSX.Element {
  const list = useQuery({
    queryKey: ['callbacks', bucket, scope],
    queryFn: () =>
      api.get<ListResponse>(
        `/callbacks?bucket=${bucket}&scope=${scope}&limit=${bucket === 'completed' ? 50 : 200}`,
      ),
    refetchOnWindowFocus: true,
  });

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  return (
    <section className="card overflow-hidden">
      <header
        className={`flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
          bucket === 'overdue' && total > 0
            ? 'bg-red-50 text-red-700'
            : 'bg-ink-50 text-ink-500'
        }`}
      >
        <div className="flex items-center gap-2">
          {bucket === 'overdue' && total > 0 && <span className="h-2 w-2 rounded-full bg-red-500" />}
          <span>
            {BUCKET_LABEL[bucket]} {total > 0 && <span className="text-ink-400">({total})</span>}
          </span>
        </div>
      </header>
      <div className="overflow-x-auto">
      <table className="w-full text-sm whitespace-nowrap">
        <thead className="bg-white text-left text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <th className="px-4 py-2 w-40">When</th>
            <th className="px-4 py-2">Lead</th>
            <th className="px-4 py-2">Notes</th>
            <th className="px-4 py-2 w-32">Last contact</th>
            <th className="px-4 py-2 w-72 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.isLoading && (
            <tr><td colSpan={5} className="px-4 py-4 text-center text-ink-500">Loading…</td></tr>
          )}
          {!list.isLoading && items.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-4 text-center text-ink-500">No callbacks due</td></tr>
          )}
          {items.map((cb) => (
            <CallbackRow key={cb.id} cb={cb} bucket={bucket} />
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function CallbackRow({ cb, bucket }: { cb: CallbackDTO; bucket: CallbackBucket }): JSX.Element {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const selectedCallerId = useSoftphone((s) => s.selectedCallerId);
  const selectedCallerProvider = useSoftphone((s) => s.selectedCallerProvider);

  const completed = cb.status !== 'open';

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['callbacks'] });
  };

  const onCall = async (): Promise<void> => {
    setError(null);
    setBusy('call');
    try {
      // Load full lead so the softphone has phone, owner, etc.
      const lead = await api.get<Lead>(`/leads/${cb.leadId}`);
      useSoftphone.getState().setLead(lead);
      useSoftphone.getState().setCallbackId(cb.id);
      const result = await softphoneService.dial({
        toRaw: lead.phoneE164,
        leadId: lead.id,
        callbackId: cb.id,
        callerId: selectedCallerId || undefined,
        callerProvider: selectedCallerProvider ?? undefined,
      });
      if (!result.ok) {
        setError(result.detail ?? result.reason ?? 'Could not start call');
        useSoftphone.getState().setCallbackId(null);
      }
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not start call'));
    } finally {
      setBusy(null);
    }
  };

  const onMarkDone = async (): Promise<void> => {
    setError(null);
    setBusy('done');
    try {
      await api.patch(`/callbacks/${cb.id}/fulfil`, {});
      invalidate();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not mark done'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <tr className="border-t border-ink-100 align-top">
        <td className="px-4 py-2 font-mono text-xs">
          <WhenLabel iso={cb.scheduledFor} bucket={bucket} status={cb.status} />
        </td>
        <td className="px-4 py-2">
          <div className="font-medium">{cb.lead.firstName} {cb.lead.lastName}</div>
          <div className="text-xs text-ink-500">
            {cb.lead.company ? `${cb.lead.company} · ` : ''}
            <span className="font-mono">{formatUKForDisplay(cb.lead.phoneE164)}</span>
          </div>
        </td>
        <td className="px-4 py-2 text-ink-600 max-w-md truncate" title={cb.notes ?? ''}>
          {cb.notes || <span className="text-ink-400">—</span>}
        </td>
        <td className="px-4 py-2 text-xs text-ink-500">
          {cb.lead.lastContactAt ? new Date(cb.lead.lastContactAt).toLocaleDateString('en-GB') : 'never'}
        </td>
        <td className="px-4 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {!completed && (
              <>
                <button
                  type="button"
                  className="btn-primary text-xs px-2 py-1"
                  onClick={onCall}
                  disabled={busy !== null}
                >
                  {busy === 'call' ? '…' : 'Call'}
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs px-2 py-1"
                  onClick={() => setSnoozeOpen(true)}
                  disabled={busy !== null}
                >
                  Snooze
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs px-2 py-1"
                  onClick={onMarkDone}
                  disabled={busy !== null}
                  title="Mark done"
                >
                  ✓
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs px-2 py-1"
                  onClick={() => setCancelOpen(true)}
                  disabled={busy !== null}
                  title="Cancel callback"
                >
                  ⋯
                </button>
              </>
            )}
            <Link
              to={`/leads?focus=${cb.leadId}`}
              className="btn-ghost text-xs px-2 py-1"
            >
              Open lead
            </Link>
          </div>
          {error && <div className="mt-1 text-xs text-red-700">{error}</div>}
        </td>
      </tr>
      {snoozeOpen && (
        <SnoozeMenu
          callbackId={cb.id}
          onClose={() => setSnoozeOpen(false)}
          onDone={() => {
            setSnoozeOpen(false);
            invalidate();
          }}
        />
      )}
      {cancelOpen && (
        <CancelMenu
          callbackId={cb.id}
          onClose={() => setCancelOpen(false)}
          onDone={() => {
            setCancelOpen(false);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function WhenLabel({
  iso,
  bucket,
  status,
}: {
  iso: string;
  bucket: CallbackBucket;
  status: CallbackDTO['status'];
}): JSX.Element {
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.round((d.getTime() - now) / 60000);
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: bucket === 'upcoming' ? 'short' : undefined,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const time = fmt.format(d);
  if (status === 'fulfilled') return <span className="text-emerald-700">✓ {time}</span>;
  if (status === 'cancelled') return <span className="text-ink-400">✕ {time}</span>;
  if (bucket === 'overdue') {
    const ago = -diffMin;
    const label = ago < 60 ? `${ago}m ago` : `${Math.round(ago / 60)}h ago`;
    return <span className="text-red-700">{label} · {time}</span>;
  }
  if (bucket === 'today') {
    if (diffMin <= 0) return <span>{time}</span>;
    if (diffMin < 60) return <span>in {diffMin}m · {time}</span>;
    return <span>{time}</span>;
  }
  return <span>{time}</span>;
}

function SnoozeMenu({
  callbackId,
  onClose,
  onDone,
}: {
  callbackId: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customAt, setCustomAt] = useState('');

  const apply = async (token: SnoozeToken): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const body: { token: SnoozeToken; customAt?: string } = { token };
      if (token === 'custom') {
        if (!customAt) {
          setError('Pick a date and time');
          setBusy(false);
          return;
        }
        body.customAt = new Date(customAt).toISOString();
      }
      await api.patch(`/callbacks/${callbackId}/snooze`, body);
      onDone();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not snooze'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td colSpan={5} className="bg-ink-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Snooze</span>
          <button type="button" className="btn-secondary text-xs" onClick={() => apply('later_today')} disabled={busy}>Later today</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => apply('tomorrow_morning')} disabled={busy}>Tomorrow 9am</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => apply('next_week')} disabled={busy}>Next Mon 9am</button>
          <input
            type="datetime-local"
            className="input text-xs"
            value={customAt}
            onChange={(e) => setCustomAt(e.target.value)}
          />
          <button type="button" className="btn-secondary text-xs" onClick={() => apply('custom')} disabled={busy || !customAt}>Custom</button>
          <button type="button" className="btn-ghost text-xs ml-auto" onClick={onClose}>Close</button>
        </div>
        {error && <div className="mt-1 text-xs text-red-700">{error}</div>}
      </td>
    </tr>
  );
}

function CancelMenu({
  callbackId,
  onClose,
  onDone,
}: {
  callbackId: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (!reason.trim()) {
      setError('Reason required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/callbacks/${callbackId}/cancel`, { reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not cancel'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td colSpan={5} className="bg-ink-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Cancel — reason</span>
          <input
            type="text"
            className="input text-sm flex-1 min-w-[200px]"
            placeholder="No longer needed (e.g. lead asked to be removed)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="button" className="btn-danger text-xs" onClick={submit} disabled={busy}>Confirm cancel</button>
          <button type="button" className="btn-ghost text-xs" onClick={onClose}>Close</button>
        </div>
        {error && <div className="mt-1 text-xs text-red-700">{error}</div>}
      </td>
    </tr>
  );
}
